import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue'
import { useInitiateAgreementSignatureMutation } from '../api/useInitiateAgreementSignatureMutation'
import { useDeclineAgreementSignatureMutation } from '../api/useDeclineAgreementSignatureMutation'
import { saveSignContext } from '../utils/agreementSignContext'
import { errorMessageOf } from '../utils/listingRequestErrorUtils'
import type { AgreementSignatureTarget } from '../types/listingRequestTypes'

// Owns the seller's own signing actions on an agreement row: starting a UAE Pass
// signing session, and declining to sign (dialog + reason + error). Sibling of
// `useAgreementShareLink`, which handles the forward-a-link-to-someone-else flow.
//
// Signing is a full-page hand-off: the return URL carries no context, so the
// target is stashed first and the sign-return page finalises from it.
//
// The property and Marketplace screens answer a funding window extension with
// this too: they pass their own path so UAE PASS returns the seller there, and a
// callback to refresh the proposal once a decline lands.
export interface AgreementSigningOptions {
  returnPath?: MaybeRefOrGetter<string>
  onDeclined?: () => void
}

export function useAgreementSigning(
  id: MaybeRefOrGetter<string>,
  options: AgreementSigningOptions = {},
) {
  const initiate = useInitiateAgreementSignatureMutation(id)
  const decline = useDeclineAgreementSignatureMutation(id)

  // The signatory whose signing session is being started (drives the row spinner).
  const pendingSignatoryId = ref<string | null>(null)
  const declineDialogVisible = ref(false)
  const declineTarget = ref<AgreementSignatureTarget | null>(null)

  const isDeclining = computed(() => decline.isPending.value)
  const declineError = computed(() =>
    decline.isError.value ? errorMessageOf(decline.error.value) : '',
  )

  function startSigning(target: AgreementSignatureTarget): void {
    pendingSignatoryId.value = target.signatoryId
    const returnPath = toValue(options.returnPath)
    saveSignContext({
      requestId: toValue(id),
      ...target,
      ...(returnPath && { returnPath }),
    })
    initiate.mutate(target, {
      onSuccess: (initiation) => window.location.assign(initiation.signUrl),
      onError: () => (pendingSignatoryId.value = null),
    })
  }

  function openDecline(target: AgreementSignatureTarget): void {
    declineTarget.value = target
    declineDialogVisible.value = true
  }

  function confirmDecline(reason: string): void {
    const target = declineTarget.value
    if (!target) return
    decline.mutate(
      { ...target, ...(reason ? { reason } : {}) },
      {
        onSuccess: () => {
          declineDialogVisible.value = false
          options.onDeclined?.()
        },
      },
    )
  }

  return {
    pendingSignatoryId,
    declineDialogVisible,
    isDeclining,
    declineError,
    startSigning,
    openDecline,
    confirmDecline,
  }
}
